import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatNumberCompact, formatCurrencyUSD, formatPercent } from '../../utils/format';
import { useSettings } from '../../contexts/SettingsContext';
import ChartExportButton from './ChartExportButton';

/**
 * Bar Chart Component
 * Displays categorical data with horizontal or vertical bars
 * Perfect for comparing values across categories
 */
const BarChart = ({ 
  data = [], 
  label = '',
  formatType = 'number',
  color = 'accent',
  orientation = 'vertical', // 'vertical' | 'horizontal'
  showComparison = false,
  comparisonData = [],
  showExport = true,
  exportFilename = 'chart'
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isCompact, setIsCompact] = useState(false);
  const chartContainerRef = useRef(null);
  const svgRef = useRef(null);

  // Get settings from context
  const settings = useSettings();

  // Check if inside compact container
  useEffect(() => {
    if (chartContainerRef.current) {
      const hasCompactParent = chartContainerRef.current.closest('.chart-container-compact') !== null;
      setIsCompact(hasCompactParent);
    }
  }, []);

  // Get user preference for chart animations from settings
  const animationsEnabled = settings.chartAnimations;
  
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;

    const width = isCompact ? 700 : 1000;
    const height = isCompact ? 300 : 400;
    const padding = { top: 40, right: 80, bottom: 60, left: 80 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Find max value for scaling
    const allValues = [
      ...data.map(d => typeof d.value === 'number' ? d.value : 0),
      ...comparisonData.map(d => typeof d.value === 'number' ? d.value : 0)
    ];
    const maxValue = Math.max(...allValues.filter(v => !isNaN(v) && isFinite(v)), 1);

    // Calculate bar dimensions
    const isVertical = orientation === 'vertical';
    const barCount = data.length;
    const barSpacing = (isVertical ? chartWidth : chartHeight) / barCount;
    const barWidth = barSpacing * 0.6; // 60% of space for bar, 40% for gap

    return {
      data: data.map((d, i) => ({
        ...d,
        value: typeof d.value === 'number' ? d.value : 0,
        label: d.label || d.name || `${i + 1}`,
        index: i
      })),
      comparisonData: comparisonData.map((d, i) => ({
        ...d,
        value: typeof d.value === 'number' ? d.value : 0,
        index: i
      })),
      maxValue,
      width,
      height,
      padding,
      chartWidth,
      chartHeight,
      barWidth,
      barSpacing,
      isVertical
    };
  }, [data, comparisonData, orientation, isCompact]);

  const formatValue = (value) => {
    switch (formatType) {
      case 'currency':
        return formatCurrencyUSD(value);
      case 'percent':
        return formatPercent(value);
      case 'number':
      default:
        return formatNumberCompact(value);
    }
  };

  if (!chartData || data.length === 0) {
    return (
      <div className="chart-container chart-empty">
        <div className="chart-label">{label}</div>
        <div className="chart-placeholder">No data available</div>
      </div>
    );
  }

  const strokeColor = '#53EF97';
  const comparisonColor = '#2e8b57';

  return (
    <div 
      className="chart-container bar-chart" 
      ref={chartContainerRef}
    >
      <div className="chart-header">
        {label && <div className="chart-label">{label}</div>}
        {showExport && (
          <ChartExportButton 
            chartRef={chartContainerRef}
            filename={exportFilename || label || 'bar-chart'}
            formats={['png', 'svg', 'pdf']}
          />
        )}
      </div>
      <svg 
        ref={svgRef}
        className="chart-svg bar-chart-svg" 
        viewBox={`0 0 ${chartData.width} ${chartData.height}`}
        preserveAspectRatio="none"
        aria-label={label || 'Bar Chart'}
      >
        <defs>
          <linearGradient id={`bar-gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.6" />
          </linearGradient>
          {showComparison && (
            <linearGradient id={`bar-gradient-comparison-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={comparisonColor} stopOpacity="0.7" />
              <stop offset="100%" stopColor={comparisonColor} stopOpacity="0.5" />
            </linearGradient>
          )}
        </defs>

        {/* Grid lines */}
        {Array.from({ length: 6 }).map((_, i) => {
          const y = chartData.padding.top + (i * chartData.chartHeight / 5);
          return (
            <line
              key={`grid-h-${i}`}
              x1={chartData.padding.left}
              y1={y}
              x2={chartData.padding.left + chartData.chartWidth}
              y2={y}
              stroke="var(--admin-card-border)"
              strokeWidth="0.5"
              opacity="0.2"
              strokeDasharray="2 2"
            />
          );
        })}

        {/* Bars */}
        {chartData.data.map((item, index) => {
          const barLength = (item.value / chartData.maxValue) * (chartData.isVertical ? chartData.chartHeight : chartData.chartWidth);
          const x = chartData.padding.left + (index * chartData.barSpacing) + (chartData.barSpacing - chartData.barWidth) / 2;
          const y = chartData.padding.top + chartData.chartHeight - barLength;
          const isHovered = hoveredIndex === index;

          return (
            <g key={`bar-${index}`}>
              {/* Main bar */}
              <rect
                x={chartData.isVertical ? x : chartData.padding.left}
                y={chartData.isVertical ? y : chartData.padding.top + (index * chartData.barSpacing) + (chartData.barSpacing - chartData.barWidth) / 2}
                width={chartData.isVertical ? chartData.barWidth : barLength}
                height={chartData.isVertical ? barLength : chartData.barWidth}
                fill={isHovered ? strokeColor : `url(#bar-gradient-${color})`}
                stroke={strokeColor}
                strokeWidth={isHovered ? '2' : '1'}
                className="bar-chart-bar"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{ 
                  cursor: 'pointer', 
                  transition: animationsEnabled ? 'all 0.2s ease' : 'none'
                }}
              />

              {/* Comparison bar (if provided) */}
              {showComparison && chartData.comparisonData[index] && (
                <rect
                  x={chartData.isVertical ? x + chartData.barWidth * 0.5 : chartData.padding.left}
                  y={chartData.isVertical ? chartData.padding.top + chartData.chartHeight - (chartData.comparisonData[index].value / chartData.maxValue) * chartData.chartHeight : chartData.padding.top + (index * chartData.barSpacing) + (chartData.barSpacing - chartData.barWidth) / 2 + chartData.barWidth * 0.5}
                  width={chartData.isVertical ? chartData.barWidth * 0.4 : (chartData.comparisonData[index].value / chartData.maxValue) * chartData.chartWidth}
                  height={chartData.isVertical ? (chartData.comparisonData[index].value / chartData.maxValue) * chartData.chartHeight : chartData.barWidth * 0.4}
                  fill={comparisonColor}
                  fillOpacity="0.6"
                  className="bar-chart-bar bar-chart-bar-comparison"
                />
              )}

              {/* Value label */}
              <text
                x={chartData.isVertical ? x + chartData.barWidth / 2 : chartData.padding.left + barLength + 10}
                y={chartData.isVertical ? y - 5 : chartData.padding.top + (index * chartData.barSpacing) + chartData.barSpacing / 2}
                fill="var(--admin-text-primary)"
                fontSize="12"
                textAnchor={chartData.isVertical ? "middle" : "start"}
                className="bar-chart-value-label"
              >
                {formatValue(item.value)}
              </text>

              {/* Category label */}
              <text
                x={chartData.isVertical ? x + chartData.barWidth / 2 : chartData.padding.left - 10}
                y={chartData.isVertical ? chartData.padding.top + chartData.chartHeight + 20 : chartData.padding.top + (index * chartData.barSpacing) + chartData.barSpacing / 2}
                fill="var(--admin-text-secondary)"
                fontSize="11"
                textAnchor={chartData.isVertical ? "middle" : "end"}
                className="bar-chart-label"
              >
                {item.label.length > 10 ? item.label.substring(0, 10) + '...' : item.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      {showComparison && comparisonData.length > 0 && (
        <div className="chart-legend">
          <div className="chart-legend-item">
            <span className="chart-legend-bar chart-legend-current"></span>
            <span>Current</span>
          </div>
          <div className="chart-legend-item">
            <span className="chart-legend-bar chart-legend-comparison"></span>
            <span>Previous</span>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {settings.showTooltips && hoveredIndex !== null && chartData.data[hoveredIndex] && (
        <div 
          className="chart-tooltip bar-chart-tooltip"
          style={{
            left: `${chartData.padding.left + (hoveredIndex * chartData.barSpacing) + chartData.barWidth / 2}px`,
            top: `${chartData.padding.top - 60}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="chart-tooltip-header">
            <div className="chart-tooltip-label">
              {chartData.data[hoveredIndex].label}
            </div>
          </div>
          <div className="chart-tooltip-content">
            <div className="chart-tooltip-item">
              <div className="chart-tooltip-value">
                {formatValue(chartData.data[hoveredIndex].value)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

BarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.number.isRequired
    })
  ).isRequired,
  label: PropTypes.string,
  formatType: PropTypes.oneOf(['number', 'currency', 'percent']),
  color: PropTypes.string,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  showComparison: PropTypes.bool,
  comparisonData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired
    })
  ),
  showExport: PropTypes.bool,
  exportFilename: PropTypes.string
};

export default BarChart;
